import * as Sentry from "@sentry/react-native";
import { router } from "expo-router";
import { ParseError, parsePhoneNumberWithError } from "libphonenumber-js";
import { useField, useForm } from "mobx-easy-form";
import { observer, Observer } from "mobx-react";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import * as yup from "yup";
import { z } from "zod";
import { Icon } from "~/components/Icon";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { TextInputV2 } from "~/components/V2/TextInputV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { environment } from "~/environment";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

// This screen is only used for existing/old clients (isNewUser=0)
// We need this screen because it is possible for some existing users to not have their email address setup
// which would case the issue with sending emails later
export const AddExistingClientDetailsScreen = observer(
  function AddExistingClientDetailsScreen() {
    const store = useStore();

    const activeUser = store.authStore.activeUser;

    assert(activeUser, "Active user must be defined");

    const errorAlert = useErrorAlertV2();
    useProgressBarStep(7);

    const { clinicId, isNewUser, isStandAloneBookingFlow } =
      useStandaloneBookingFlow();

    const form = useForm({
      onSubmit: async ({ values }) => {
        const valuesSchema = z.object({
          name: z.string(),
          phoneNumber: z.string(),
          email: z.string(),
          address: z.string(),
          postalNumber: z.string(),
          city: z.string(),
        });

        const valuesParsed = valuesSchema.parse(values);

        store.bookingStore.setExistingClientDetails({
          email: valuesParsed.email,
          phoneNumber: valuesParsed.phoneNumber,
          streetAddress: valuesParsed.address,
          zipCode: valuesParsed.postalNumber,
          city: valuesParsed.city,
        });

        // this screen is for now only used in standalone flow so this check doesnt make sense at the moment
        // but we can keep it just in case we decide to use this screen in other flows
        if (isStandAloneBookingFlow) {
          router.navigate({
            pathname: "/(standalone)/booking/overview",
            params: {
              clinicId,
              isNewUser: isNewUser ? "1" : "0",
            },
          });
        } else {
          router.navigate({
            pathname: "/(app)/book-appointment/overview",
          });
        }
      },
    });

    // Functions to get initial values for the form fields
    const getInitialFirstName = useCallback(() => {
      if (!!activeUser.firstName && !!activeUser.lastName) {
        return `${activeUser.firstName} ${activeUser.lastName}`;
      } else if (__DEV__) {
        return "John Doe";
      } else {
        return "";
      }
    }, [activeUser.firstName, activeUser.lastName]);
    const getInitialPhoneNumber = useCallback(() => {
      if (activeUser.phoneNumber) {
        return activeUser.phoneNumber;
      } else if (__DEV__) {
        return "+46726416273";
      } else {
        return environment.SUPPORT_PHONE_NUMBER_COUNTRY_CODE;
      }
    }, [activeUser.phoneNumber]);
    const getInitialEmail = useCallback((): string => {
      return "";
    }, []);
    const getInitialAddress = useCallback(() => {
      if (activeUser.streetAddress) {
        return activeUser.streetAddress;
      } else if (__DEV__) {
        return "Kungsgatan";
      } else {
        return "";
      }
    }, [activeUser.streetAddress]);
    const getInitialZipCode = useCallback(() => {
      if (activeUser.zipCode) {
        return activeUser.zipCode;
      } else if (__DEV__) {
        return "753 21";
      } else {
        return "";
      }
    }, [activeUser.zipCode]);
    const getInitialCity = useCallback(() => {
      if (activeUser.city) {
        return activeUser.city;
      } else if (__DEV__) {
        return "Uppsala";
      } else {
        return "";
      }
    }, [activeUser.city]);
    // End of functions to get initial values for the form fields

    const name = useField({
      id: "name",
      form,
      initialValue: getInitialFirstName(),
      validationSchema: yup.string().required(
        intl.formatMessage({
          id: "9gOiO+",
          defaultMessage: "Name is required",
          description: "Error message for empty name",
        }),
      ),
    });

    const phoneNumber = useField(
      {
        id: "phoneNumber",
        form,
        initialValue: getInitialPhoneNumber(),
        validate: (value) => {
          if (!!value && value.trim()) {
            try {
              const parsedValue = parsePhoneNumberWithError(value, {
                extract: false,
              });

              return {
                error: undefined,
                parsed: parsedValue.formatInternational(),
              };
            } catch (error) {
              if (error instanceof ParseError) {
                if (error.message === "TOO_LONG") {
                  return {
                    error: intl.formatMessage({
                      id: "pB8SjL",
                      description: "Error message for too long phone number",
                      defaultMessage: "The phone number is too long",
                    }),
                    parsed: undefined,
                  };
                } else if (error.message === "TOO_SHORT") {
                  return {
                    error: intl.formatMessage({
                      id: "l8lO/0",
                      description: "Error message for too short phone number",
                      defaultMessage: "The phone number is too short",
                    }),
                    parsed: undefined,
                  };
                } else {
                  return {
                    error: intl.formatMessage({
                      id: "N/1f86",
                      description: "Error message for invalid phone number",
                      defaultMessage:
                        "The phone number you entered is not valid",
                    }),
                    parsed: undefined,
                  };
                }
              } else {
                errorAlert(error);
                Sentry.captureException(error);
                return {
                  error: intl.formatMessage({
                    id: "N/1f86",
                    description: "Error message for invalid phone number",
                    defaultMessage: "The phone number you entered is not valid",
                  }),
                  parsed: undefined,
                };
              }
            }
          }
          return {
            error: intl.formatMessage({
              id: "KBojtB",
              description: "Error message for empty phone number",
              defaultMessage: "Phone number is required",
            }),
            parsed: undefined,
          };
        },
      },
      [],
    );
    const email = useField({
      id: "email",
      form,
      initialValue: getInitialEmail(),
      validationSchema: yup
        .string()
        .email(
          intl.formatMessage({
            id: "295JUa",
            defaultMessage: "The email you entered is invalid",
            description: "Error message for invalid email",
          }),
        )
        .required(
          intl.formatMessage({
            id: "GoWwI6",
            defaultMessage: "Email is required",
            description: "Error message for empty email",
          }),
        ),
    });
    const address = useField({
      id: "address",
      form,
      initialValue: getInitialAddress(),
      validationSchema: yup.string().required(
        intl.formatMessage({
          id: "L6ID4a",
          defaultMessage: "Address is required",
          description: "Error message for empty address",
        }),
      ),
    });
    const postalNumber = useField({
      id: "postalNumber",
      form,
      initialValue: getInitialZipCode(),
      validationSchema: yup.string().required(
        intl.formatMessage({
          id: "gFpun4",
          defaultMessage: "Postal number is required",
          description: "Error message for empty postal number",
        }),
      ),
    });
    const city = useField({
      id: "city",
      form,
      initialValue: getInitialCity(),
      validationSchema: yup.string().required(
        intl.formatMessage({
          id: "a8h+Yh",
          defaultMessage: "City is required",
          description: "Error message for empty city",
        }),
      ),
    });

    return (
      <View flex colorWhite>
        <View paddingVertical32 paddingHorizontal24>
          <BrandText sizeH1 weightExtraBold alignCenter colorBlack>
            <FormattedMessage
              defaultMessage="Your information"
              id="M/DGFF"
              description="Title for client details screen"
            />
          </BrandText>
          <Spacer extraExtraLarge />

          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "5VPo0E",
                    defaultMessage: "Name",
                    description: "First name label",
                  })}
                  value={name.state.value}
                  disabled={!!activeUser.firstName && !!activeUser.lastName}
                  onFocus={name.actions.onFocus}
                  onBlur={name.actions.onBlur}
                  onChangeText={name.actions.onChange}
                  rightComponent={
                    !!activeUser.firstName &&
                    !!activeUser.lastName && (
                      <Icon
                        name="lock-outline"
                        size={24}
                        color={styleConstantsV2.colorBlack}
                      />
                    )
                  }
                  isError={!!name.computed.ifWasEverBlurredThenError}
                />
              );
            }}
          </Observer>

          <Spacer large />
          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "DWtSWO",
                    defaultMessage: "Phone number",
                    description: "Phone number label",
                  })}
                  value={phoneNumber.state.value}
                  onBlur={phoneNumber.actions.onBlur}
                  onFocus={phoneNumber.actions.onFocus}
                  onChangeText={phoneNumber.actions.onChange}
                  isError={!!phoneNumber.computed.ifWasEverBlurredThenError}
                  textBelowComponent={
                    phoneNumber.computed.ifWasEverBlurredThenError ? (
                      <BodyText sizeSmall colorError>
                        {phoneNumber.computed.error}
                      </BodyText>
                    ) : undefined
                  }
                />
              );
            }}
          </Observer>
          <Spacer large />
          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "dRMELz",
                    defaultMessage: "E-mail",
                    description: "Email label",
                  })}
                  value={email.state.value}
                  onBlur={email.actions.onBlur}
                  onFocus={email.actions.onFocus}
                  onChangeText={email.actions.onChange}
                  isError={!!email.computed.error}
                  textBelowComponent={
                    email.computed.ifWasEverBlurredThenError ? (
                      <BodyText sizeSmall colorError>
                        {email.computed.error}
                      </BodyText>
                    ) : undefined
                  }
                />
              );
            }}
          </Observer>
          <Spacer large />
          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "Oj0iQH",
                    defaultMessage: "Address",
                    description: "Address label",
                  })}
                  value={address.state.value}
                  onBlur={address.actions.onBlur}
                  onFocus={address.actions.onFocus}
                  onChangeText={address.actions.onChange}
                  isError={!!address.computed.ifWasEverBlurredThenError}
                  textBelowComponent={
                    address.computed.ifWasEverBlurredThenError ? (
                      <BodyText sizeSmall colorError>
                        {address.computed.error}
                      </BodyText>
                    ) : undefined
                  }
                />
              );
            }}
          </Observer>
          <Spacer large />
          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "0nJ1MV",
                    defaultMessage: "Postal number",
                    description: "Postal number input label",
                  })}
                  value={postalNumber.state.value}
                  onBlur={postalNumber.actions.onBlur}
                  onFocus={postalNumber.actions.onFocus}
                  onChangeText={postalNumber.actions.onChange}
                  isError={!!postalNumber.computed.ifWasEverBlurredThenError}
                  textBelowComponent={
                    postalNumber.computed.ifWasEverBlurredThenError ? (
                      <BodyText sizeSmall colorError>
                        {postalNumber.computed.error}
                      </BodyText>
                    ) : undefined
                  }
                />
              );
            }}
          </Observer>
          <Spacer large />
          <Observer>
            {() => {
              return (
                <TextInputV2
                  label={intl.formatMessage({
                    id: "mMWP1r",
                    defaultMessage: "City",
                    description: "City label",
                  })}
                  value={city.state.value}
                  onBlur={city.actions.onBlur}
                  onFocus={city.actions.onFocus}
                  onChangeText={city.actions.onChange}
                  isError={!!city.computed.ifWasEverBlurredThenError}
                  textBelowComponent={
                    city.computed.ifWasEverBlurredThenError ? (
                      <BodyText sizeSmall colorError>
                        {city.computed.error}
                      </BodyText>
                    ) : undefined
                  }
                />
              );
            }}
          </Observer>
        </View>
        <View
          flex
          flexDirectionColumnReverse
          paddingHorizontal24
          paddingVertical32
          colorWhite
        >
          <Observer>
            {() => {
              return (
                <ButtonV2
                  title={intl.formatMessage({
                    id: "3Wcqsy",
                    defaultMessage: "Next",
                    description: "Next button",
                  })}
                  onPress={form.actions.submit}
                  disabled={form.computed.isError && form.state.submitCount > 0}
                />
              );
            }}
          </Observer>
        </View>
      </View>
    );
  },
);

// If any required data from earlier in the booking process is missing, redirect to the first step
export const AddExistingClientDetailsScreenWithBookingData = withBookingData(
  AddExistingClientDetailsScreen,
  [
    "clinicId",
    "patientId",
    "reasonId",
    "practitionerId",
    "start",
    "end",
    "clientNotes",
  ],
);
